import React from "react";
import { Utensils, Timer, Play, Pause, RotateCcw } from "lucide-react";
import { getScaledQuantity, findStepMedia, findStepIngredients } from "../utils/recipeUtils";

const StepCard = ({ step, index, recipe, servings, isActive, onClick }) => {

    // Timer Logic
    const [timeLeft, setTimeLeft] = React.useState(step.durationSeconds || 0);
    const [isRunning, setIsRunning] = React.useState(false);
    const [isFinished, setIsFinished] = React.useState(false);

    React.useEffect(() => {
        setTimeLeft(step.durationSeconds || 0);
        setIsRunning(false);
        setIsFinished(false);
    }, [step.durationSeconds]);

    React.useEffect(() => {
        let interval = null;
        if (isRunning && timeLeft > 0) {
            interval = setInterval(() => {
                setTimeLeft((prevTime) => {
                    if (prevTime <= 1) {
                        setIsFinished(true);
                        setIsRunning(false);
                        return 0;
                    }
                    return prevTime - 1;
                });
            }, 1000);
        } else if (timeLeft === 0) {
            setIsFinished(true);
            setIsRunning(false);
        }
        return () => clearInterval(interval);
    }, [isRunning, timeLeft]);

    const toggleTimer = (e) => {
        e.stopPropagation(); // Prevent card click
        if (timeLeft > 0) {
            setIsRunning(!isRunning);
        } else if (isFinished) {
            // Reset
            setTimeLeft(step.durationSeconds || 0);
            setIsFinished(false);
            setIsRunning(true);
        }
    };

    const formatTime = (seconds) => {
        const m = Math.floor(seconds / 60);
        const s = seconds % 60;
        return `${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`;
    };

    const stepMedia = findStepMedia(recipe.media, step);
    const stepIngredients = findStepIngredients(recipe.ingredients, step);
    const showOverlay = stepMedia && stepIngredients.length > 0;
    const hasTimer = step.durationSeconds > 0;

    return (
        <div
            style={{
                scrollSnapAlign: 'start',
                opacity: isActive ? 1 : 0.6,
                transition: 'opacity 0.3s ease'
            }}
            onClick={onClick}
        >
            <div className={`card h-100 rounded-4 border bg-white overflow-hidden d-flex flex-column transition-all ${isActive ? 'shadow-lg border-primary' : 'shadow-sm border-light'}`}>
                <div className="card-header bg-white border-0 pt-3 px-3 pb-0 d-flex justify-content-between align-items-center">
                    <div className="d-flex align-items-center gap-3">
                        <div className={`rounded-circle d-flex align-items-center justify-content-center fw-bold small ${isActive ? 'bg-primary text-white' : 'bg-light text-muted'}`} style={{ width: '28px', height: '28px' }}>
                            {step.stepNumber || index + 1}
                        </div>
                        <h6 className={`fw-bold font-sans m-0 text-uppercase small ${isActive ? 'text-primary' : 'text-muted'}`}>Schritt {step.stepNumber || index + 1}</h6>
                    </div>
                    {hasTimer && (
                        <button
                            onClick={toggleTimer}
                            className={`btn btn-sm d-flex align-items-center gap-2 rounded-pill px-3 py-1 border transition-all ${isFinished ? 'btn-danger text-white' :
                                isRunning ? 'btn-primary text-white' :
                                    'btn-light text-primary border-primary border-opacity-25'
                                }`}
                            style={{ fontSize: '0.85rem' }}
                        >
                            {isFinished ? <RotateCcw size={14} /> : isRunning ? <Pause size={14} /> : <Play size={14} />}
                            <span className="fw-bold font-monospace">{formatTime(timeLeft)}</span>
                        </button>
                    )}
                </div>

                <div className="card-body p-3 d-flex flex-column flex-grow-1" style={{ minHeight: 0 }}>
                    {/* Step Ingredients Banner (Horizontal) - Only if NO overlay */}
                    {!showOverlay && stepIngredients.length > 0 && (
                        <div className="bg-light bg-opacity-50 px-3 py-2 border-bottom d-flex flex-column gap-2 mb-3 rounded-3">
                            <h6 className="small fw-bold text-uppercase text-muted mb-0 d-flex align-items-center gap-2" style={{ fontSize: '0.7rem' }}>
                                <Utensils size={12} className="text-primary" /> Benötigt
                            </h6>
                            <div className="d-flex flex-wrap gap-2">
                                {stepIngredients.map((ing, iIdx) => (
                                    <div key={iIdx} className="bg-white border rounded-pill px-2 py-1 d-flex align-items-center gap-2 shadow-sm" style={{ maxWidth: '100%' }}>
                                        <div className="d-flex align-items-center gap-1 font-sans text-dark small" style={{ fontSize: '0.8rem' }}>
                                            <span className="fw-bold">{getScaledQuantity(ing.quantity, recipe.servings, servings)}</span>
                                            <span>{ing.unit}</span>
                                            <span className="fw-medium text-muted">{ing.name}</span>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    {stepMedia && (
                        <div className="mb-3 rounded-3 overflow-hidden position-relative" style={{ height: '160px', flexShrink: 0 }}>
                            <img src={stepMedia.url} alt={`Schritt ${step.stepNumber}`} className="w-100 h-100 object-fit-cover" />

                            {/* Ingredients Overlay - Always if media exists */}
                            {showOverlay && (
                                <div className="position-absolute top-0 start-0 p-2 d-flex flex-column gap-1" style={{ maxWidth: '90%' }}>
                                    {stepIngredients.map((ing, iIdx) => (
                                        <div key={iIdx} className="bg-dark bg-opacity-75 text-white px-2 py-1 rounded-3 small backdrop-blur-sm shadow-sm border border-white border-opacity-25" style={{ width: 'fit-content' }}>
                                            <div className="d-flex align-items-center gap-1" style={{ lineHeight: '1.2', fontSize: '0.8rem' }}>
                                                <span className="fw-bold text-nowrap">{getScaledQuantity(ing.quantity, recipe.servings, servings)} {ing.unit}</span>
                                                <span className="text-truncate" style={{ maxWidth: '120px' }}>{ing.name}</span>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    )}
                    <p className="font-sans text-dark mt-auto" style={{ fontSize: '1rem', lineHeight: '1.5', color: '#4a4a4a' }}>
                        {step.instruction}
                    </p>
                </div>
            </div>
        </div>
    );
};

export default StepCard;
